import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { XIcon as X, CheckIcon as Check, ProhibitIcon as Prohibit, ArrowCounterClockwiseIcon as Reopen, CircleNotchIcon as Spinner, WarningIcon as Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { decideDisposition, getDisposition, patchDisposition } from "@/lib/api";
import {
  DISPOSITIONS, DISPOSITION_HELP, ORIGIN_LABEL, SIGNAL_LABEL, STALE_CLASS, STALE_LABEL, TREATMENTS, TREATMENT_HELP,
  objectLabel, signalLabel, stateClass, stateLabel,
} from "@/lib/dispositionConstants";
import { fmtDateTime } from "@/lib/provenanceDisplay";

/** Side panel: edit axes/rationale, validate / reject / reopen, see evidence batches. */
export default function DispositionDetail({ workspaceId, dispositionId, canEdit, onClose, onChanged }) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ technical_treatment: "", migration_disposition: "", rationale: "" });
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!dispositionId) return;
    setLoading(true);
    getDisposition(workspaceId, dispositionId)
      .then((d) => {
        setRow(d);
        setForm({
          technical_treatment: d.technical_treatment || "",
          migration_disposition: d.migration_disposition || "",
          rationale: d.rationale || "",
        });
        setNote(d.decision_note || "");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load disposition."))
      .finally(() => setLoading(false));
  }, [workspaceId, dispositionId]);

  if (!dispositionId) return null;

  const dirty = row && (
    form.technical_treatment !== (row.technical_treatment || "") ||
    form.migration_disposition !== (row.migration_disposition || "") ||
    form.rationale !== (row.rationale || "")
  );

  const apply = (updated, msg) => {
    setRow(updated);
    setForm({
      technical_treatment: updated.technical_treatment || "",
      migration_disposition: updated.migration_disposition || "",
      rationale: updated.rationale || "",
    });
    toast.success(msg);
    onChanged?.(updated);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        technical_treatment: form.technical_treatment || null,
        migration_disposition: form.migration_disposition || null,
        rationale: form.rationale,
      };
      const updated = await patchDisposition(workspaceId, dispositionId, body);
      apply(updated, updated.reopened ? "Saved — row reopened as proposed (axes changed)." : "Saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const decide = async (decision) => {
    setSaving(true);
    try {
      const updated = await decideDisposition(workspaceId, dispositionId, decision, note || null);
      apply(updated, decision === "proposed" ? "Reopened." : `Marked ${decision}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="ml-card p-5 space-y-4 lg:sticky lg:top-24" data-testid="disposition-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{row?.object_kind || "disposition"}</div>
          <div className="ml-mono text-base font-semibold">{row ? objectLabel(row) : "…"}</div>
        </div>
        <button onClick={onClose} className="ml-btn-ghost p-1" aria-label="Close" data-testid="disposition-detail-close"><X size={14} /></button>
      </div>

      {loading || !row ? (
        <div className="text-sm text-[var(--ml-text-secondary)] flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${stateClass(row.state)}`} data-testid="detail-state-pill">
              {stateLabel(row.state)}
            </span>
            {row.stale && (
              <span className={`ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 ${STALE_CLASS}`} data-testid="detail-stale-pill">
                <Warning size={10} weight="bold" /> {STALE_LABEL}
              </span>
            )}
            <span className="text-[11px] text-[var(--ml-text-secondary)]">
              {row.signal ? `${SIGNAL_LABEL[row.signal] || row.signal} · ` : ""}{ORIGIN_LABEL[row.origin] || row.origin}
            </span>
          </div>
          {row.stale && (
            <div className="border border-[#ff832b]/50 bg-[#ff832b]/10 p-3 space-y-1.5 text-xs text-[#8a3800]" data-testid="detail-stale-warning">
              <div className="font-semibold flex items-center gap-1.5"><Warning size={13} weight="fill" /> Stale — driving signal no longer fires</div>
              <p className="leading-snug" data-testid="detail-stale-reason">{row.stale_reason}</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                <dt className="ml-mono uppercase tracking-widest text-[10px] opacity-80">Previous signal</dt>
                <dd data-testid="detail-stale-previous">{signalLabel(row.previous_signal || row.signal)}</dd>
                <dt className="ml-mono uppercase tracking-widest text-[10px] opacity-80">Signal now</dt>
                <dd data-testid="detail-stale-current">{row.current_signal ? signalLabel(row.current_signal) : "no deterministic rule fires"}</dd>
                <dt className="ml-mono uppercase tracking-widest text-[10px] opacity-80">Detected</dt>
                <dd data-testid="detail-stale-detected">{fmtDateTime(row.stale_detected_at)}{row.stale_detected_by ? ` · ${row.stale_detected_by}` : ""}</dd>
              </dl>
              <p className="text-[11px] opacity-90">Original treatment, disposition, rationale and evidence are unchanged. The flag clears only when the same signal fires again on re-seed.</p>
            </div>
          )}
          {row.last_signal_checked_at && (
            <p className="text-[11px] text-[var(--ml-text-secondary)]" data-testid="detail-last-checked">
              Signal last checked {fmtDateTime(row.last_signal_checked_at)}
              {!row.stale && row.current_signal ? ` · ${signalLabel(row.current_signal)} still fires` : ""}
              {!row.stale && !row.current_signal && row.origin !== "deterministic" ? " · no deterministic rule fires for this object" : ""}
            </p>
          )}
          {row.state === "proposed" && (
            <p className="text-xs text-[#8a6d00] leading-snug" data-testid="detail-proposed-note">
              Proposal only — {row.origin === "deterministic" ? "derived by a deterministic rule from imported evidence" : "drafted by a consultant"}. Not a decision until validated.
            </p>
          )}
          {row.decided_by && (
            <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="detail-decided-by">
              {stateLabel(row.state)} by <strong>{row.decided_by}</strong> · {fmtDateTime(row.decided_at)}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs space-y-1">
              <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Technical treatment</span>
              <select className="ml-input" value={form.technical_treatment} disabled={!canEdit} data-testid="detail-treatment-select"
                      onChange={(e) => setForm((f) => ({ ...f, technical_treatment: e.target.value }))}>
                <option value="">— undecided —</option>
                {TREATMENTS.map((t) => <option key={t} value={t} title={TREATMENT_HELP[t]}>{t}</option>)}
              </select>
            </label>
            <label className="text-xs space-y-1">
              <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Migration disposition</span>
              <select className="ml-input" value={form.migration_disposition} disabled={!canEdit} data-testid="detail-disposition-select"
                      onChange={(e) => setForm((f) => ({ ...f, migration_disposition: e.target.value }))}>
                <option value="">— undecided —</option>
                {DISPOSITIONS.map((d) => <option key={d} value={d} title={DISPOSITION_HELP[d]}>{d}</option>)}
              </select>
            </label>
          </div>
          <label className="text-xs space-y-1 block">
            <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Rationale</span>
            <textarea className="ml-textarea min-h-[96px]" value={form.rationale} disabled={!canEdit} data-testid="detail-rationale"
                      onChange={(e) => setForm((f) => ({ ...f, rationale: e.target.value }))} />
          </label>
          {canEdit && (
            <button className="ml-btn-secondary" disabled={!dirty || saving} onClick={save} data-testid="detail-save-btn">
              {saving ? "Saving…" : "Save changes"}
            </button>
          )}

          <div className="border-t border-[var(--ml-border)] pt-3 space-y-2">
            <label className="text-xs space-y-1 block">
              <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Decision note</span>
              <textarea className="ml-textarea min-h-[60px]" value={note} disabled={!canEdit} data-testid="detail-decision-note"
                        placeholder="Why this is validated / rejected — client owner, meeting, caveat…" onChange={(e) => setNote(e.target.value)} />
            </label>
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                {row.state !== "validated" && (
                  <button className="ml-btn-primary flex items-center gap-1.5" disabled={saving} onClick={() => decide("validated")} data-testid="detail-validate-btn">
                    <Check size={14} weight="bold" /> Validate
                  </button>
                )}
                {row.state !== "rejected" && (
                  <button className="ml-btn-secondary flex items-center gap-1.5 text-[var(--ml-error)]" disabled={saving} onClick={() => decide("rejected")} data-testid="detail-reject-btn">
                    <Prohibit size={14} weight="bold" /> Reject
                  </button>
                )}
                {row.state !== "proposed" && (
                  <button className="ml-btn-ghost flex items-center gap-1.5" disabled={saving} onClick={() => decide("proposed")} data-testid="detail-reopen-btn">
                    <Reopen size={14} weight="bold" /> Reopen
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-[var(--ml-border)] pt-3">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
              Evidence ({row.evidence_refs.length} import batch{row.evidence_refs.length === 1 ? "" : "es"})
            </div>
            {row.evidence_refs.length === 0 ? (
              <p className="text-xs text-[var(--ml-text-secondary)]">No import batches linked{row.origin === "manual" ? " — consultant-authored row." : "."}</p>
            ) : (
              <ul className="text-xs space-y-1" data-testid="detail-evidence-list">
                {row.evidence_refs.map((bid) => {
                  const b = row.batches?.[bid];
                  return (
                    <li key={bid} className="flex items-center justify-between gap-2">
                      <span className="ml-mono">
                        {b ? b.source_command : "batch"}{b?.status === "legacy_backfill" ? " · legacy" : b?.file_sha256_short ? ` · ${b.file_sha256_short.slice(0, 7)}` : ""}
                        {b?.imported_at ? <span className="text-[var(--ml-text-secondary)]"> · {fmtDateTime(b.imported_at).split(",")[0]}</span> : null}
                      </span>
                      <Link to={`/workspaces/${workspaceId}/metadata/imports?batch=${bid}`} className="text-[var(--ml-primary)] hover:underline whitespace-nowrap" data-testid={`evidence-link-${bid}`}>
                        open →
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
